import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CATEGORY_ICONS } from '@cr/shared';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  LocalizedInput,
  Modal,
  Select,
  type Localized,
} from '@/components/ui';

interface CategoryNode {
  id: string;
  parentId: string | null;
  level: number;
  name: Localized;
  slug: string;
  imageUrl: string | null;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  productsCount: number;
  children: CategoryNode[];
}

/** Что уходит в API при сохранении категории. */
interface CategoryForm {
  name: Localized;
  imageUrl: string;
  icon: string;
}

const MAX_LEVEL = 3;

/** Подписи для набора из @cr/shared — сам список закрыт и общий с Mini App. */
const ICON_LABELS: Record<string, string> = {
  sparkles: 'Праздник',
  crown: 'Свадьба',
  female: 'Женское',
  male: 'Мужское',
  baby: 'Детское',
  drama: 'Костюмы',
  watch: 'Аксессуары',
  shirt: 'Повседневное',
};

export default function CategoriesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ node?: CategoryNode; parentId?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: tree = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/admin/categories')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['categories'] });

  const save = useMutation({
    mutationFn: async ({
      id,
      parentId,
      form,
    }: {
      id?: string;
      parentId?: string;
      form: CategoryForm;
    }) => {
      // Пустые строки шлём как null: иначе в базе осядет '' и Mini App примет её за картинку
      const body = {
        name: form.name,
        imageUrl: form.imageUrl.trim() || null,
        icon: form.icon || null,
      };

      return id
        ? api.patch(`/admin/categories/${id}`, body)
        : api.post('/admin/categories', { ...body, parentId });
    },
    onSuccess: () => {
      setEditing(null);
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const toggle = useMutation({
    mutationFn: (node: CategoryNode) =>
      api.patch(`/admin/categories/${node.id}`, { isActive: !node.isActive }),
    onSuccess: invalidate,
    onError: (e) => setError(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/categories/${id}`),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const move = useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      api.patch(`/admin/categories/${id}/move`, { parentId }),
    onSuccess: invalidate,
    onError: (e) => setError(errorMessage(e)),
  });

  const flat = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.flatMap((n) => [n, ...flat(n.children)]);

  function renderNode(node: CategoryNode) {
    return (
      <li key={node.id}>
        <div
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-900"
          style={{ paddingLeft: `${(node.level - 1) * 20 + 8}px` }}
        >
          <span className={`text-sm ${node.isActive ? '' : 'text-neutral-400 line-through'}`}>
            {node.name.ru ?? node.name.uz ?? node.name.en}
          </span>
          <span className="text-xs text-neutral-400">/{node.slug}</span>
          {node.productsCount > 0 && <Badge>{node.productsCount} тов.</Badge>}
          {!node.imageUrl && !node.icon && (
            <Badge tone="amber" title="В Mini App категория покажется заглушкой">
              без картинки
            </Badge>
          )}

          <div className="ml-auto flex items-center gap-1">
            {node.level < MAX_LEVEL && (
              <Button
                variant="ghost"
                onClick={() => setEditing({ parentId: node.id })}
                title="Добавить подкатегорию"
              >
                +
              </Button>
            )}
            <Button variant="ghost" onClick={() => setEditing({ node })}>
              Изм.
            </Button>
            <Button variant="ghost" onClick={() => toggle.mutate(node)}>
              {node.isActive ? 'Скрыть' : 'Включить'}
            </Button>
            {node.parentId && (
              <Button
                variant="ghost"
                onClick={() => move.mutate({ id: node.id, parentId: null })}
                title="В корень"
              >
                ↰
              </Button>
            )}
            <Button variant="ghost" onClick={() => remove.mutate(node.id)} className="text-red-600">
              Удал.
            </Button>
          </div>
        </div>

        {node.children.length > 0 && <ul>{node.children.map(renderNode)}</ul>}
      </li>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Категории</h1>
          <p className="mt-1 text-sm text-neutral-500">
            До трёх уровней: категория → подкатегория → подподкатегория
          </p>
        </div>
        <Button onClick={() => setEditing({})}>Новая категория</Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Card>
        {isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : tree.length === 0 ? (
          <p className="text-sm text-neutral-500">Категорий пока нет</p>
        ) : (
          <ul>{tree.map(renderNode)}</ul>
        )}
      </Card>

      <CategoryModal
        state={editing}
        parents={flat(tree)}
        pending={save.isPending}
        onClose={() => setEditing(null)}
        onSubmit={(form) =>
          save.mutate({ id: editing?.node?.id, parentId: editing?.parentId, form })
        }
      />
    </div>
  );
}

function CategoryModal({
  state,
  parents,
  pending,
  onClose,
  onSubmit,
}: {
  state: { node?: CategoryNode; parentId?: string } | null;
  parents: CategoryNode[];
  pending: boolean;
  onClose: () => void;
  onSubmit: (form: CategoryForm) => void;
}) {
  const [form, setForm] = useState<CategoryForm>({ name: {}, imageUrl: '', icon: '' });
  const [initialised, setInitialised] = useState<string | null>(null);

  // Инициализируем форму один раз на открытие, иначе ввод затирался бы ререндером
  const key = state ? (state.node?.id ?? state.parentId ?? 'root') : null;
  if (key && initialised !== key) {
    setForm({
      name: state?.node?.name ?? {},
      imageUrl: state?.node?.imageUrl ?? '',
      icon: state?.node?.icon ?? '',
    });
    setInitialised(key);
  }

  const parentName = state?.parentId ? parents.find((p) => p.id === state.parentId)?.name.ru : null;
  const patch = (fields: Partial<CategoryForm>) => setForm((prev) => ({ ...prev, ...fields }));

  return (
    <Modal
      open={Boolean(state)}
      title={state?.node ? 'Изменить категорию' : 'Новая категория'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {parentName && (
          <p className="text-sm text-neutral-500">
            Родитель: <span className="font-medium">{parentName}</span>
          </p>
        )}

        <LocalizedInput label="Название" value={form.name} onChange={(name) => patch({ name })} />
        <p className="text-xs text-neutral-500">
          Slug формируется автоматически из русского названия
        </p>

        <Field label="Картинка" hint="Ссылка на изображение; показывается в Mini App на плитке">
          <Input
            value={form.imageUrl}
            onChange={(e) => patch({ imageUrl: e.target.value })}
            placeholder="https://…"
          />
        </Field>

        <Field label="Иконка" hint="Используется, когда картинки нет">
          <Select value={form.icon} onChange={(e) => patch({ icon: e.target.value })}>
            <option value="">Без иконки</option>
            {CATEGORY_ICONS.map((icon) => (
              <option key={icon} value={icon}>
                {ICON_LABELS[icon] ?? icon}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button disabled={pending} onClick={() => onSubmit(form)}>
            {pending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
